import { Injectable, signal } from '@angular/core';

/**
 * PromptLaunchService
 *
 * A simple signal-based service that bridges the Prompt Library "Run" action
 * with the NewChatComponent composer.
 *
 * Flow:
 *  1. User clicks "Run" on a prompt card → PromptLibraryComponent calls
 *     `promptLaunchService.setPendingPrompt(text)` then navigates to `/bob`.
 *  2. NewChatComponent reads `pendingPrompt()` on init, pre-fills the input,
 *     and immediately calls `promptLaunchService.clear()` so the signal does
 *     not persist across multiple navigations.
 */
@Injectable({ providedIn: 'root' })
export class PromptLaunchService {
    /** The prompt text to pre-fill, or null if none is pending. */
    private _pending = signal<string | null>(null);
    /** The library prompt _id, so the backend can skip re-enhancement. */
    private _pendingId = signal<string | null>(null);

    /** Read-only accessor for the pending prompt text. */
    readonly pendingPrompt = this._pending.asReadonly();
    /** Read-only accessor for the pending prompt library ID. */
    readonly pendingPromptId = this._pendingId.asReadonly();

    /**
     * Set the prompt text (and optional library ID) to pre-fill in the composer.
     * Pass `promptId` so the backend can skip similarity search and re-enhancement.
     */
    setPendingPrompt(text: string, promptId?: string): void {
        this._pending.set(text);
        this._pendingId.set(promptId ?? null);
    }

    /** Clear both pending signals (called by NewChatComponent after reading). */
    clear(): void {
        this._pending.set(null);
        this._pendingId.set(null);
    }
}
